import {
  createFileRoute,
  redirect,
  useNavigate,
  useRouter,
  useSearch,
} from "@tanstack/react-router";
import { useMemo, type JSX } from "react";

import { graphql } from "@repo/graphql-types/gql";
import ExclamationCircle from "@repo/icons/default/ExclamationCircle";
import HelpCircle from "@repo/icons/default/HelpCircle";
import Left from "@repo/icons/default/Left";
import StarsLightSparkle from "@repo/icons/default/StarsLightSparkle";
import {
  CpsAlertV2IconArea,
  CpsAlertV2Root,
  CpsAlertV2TextArea,
  CpsAlertV2Title,
} from "corpus";

import { ContactsHistoryEssentialMessageDrawer } from "@/components/assist/assist-contacts-history/contacts-history-essential-message-drawer";
import { EssentialContactsHistoryFilterContainer } from "@/components/assist/assist-contacts-history/essential-contacts-history-filter-container";
import { AsyncDataWrapper } from "@/components/ui/async-data-wrapper";
import { Button } from "@/components/ui/button/button";
import {
  EmptyStateIcon,
  EmptyStateRoot,
  EmptyStateTitle,
} from "@/components/ui/empty-state/empty-state";
import {
  HeaderButton,
  HeaderRoot,
  HeaderTitle,
} from "@/components/ui/layout/header/header";
import { Page } from "@/components/ui/layout/page/page";
import {
  PageTitleDescription,
  PageTitleMainContent,
  PageTitleRoot,
} from "@/components/ui/layout/page-title/page-title-root";
import { Item, ItemContent, ItemMainContent, ListRoot } from "@/components/ui/list";
import { OriginChannelType } from "@/enums/customer-contact/origin-channel-type";
import { resolveContactsHistoryDates } from "@/hooks/use-contacts-history-filter";
import { useGraphQL } from "@/hooks/use-graphql";
import { isEssentialUser } from "@/lib/assist-guard";
import { getContactPhoneNumber } from "@/lib/assist-utils";
import { FIVE_MINUTES_STALE_TIME } from "@/lib/constants/dashboards-customer-overview";
import { ServicePackageEnum } from "@/lib/enums/service-package-enum";
import {
  convertDateTimeIgnoringTimezone,
  formatDateAndTimeIgnoringTimezone,
  formatIgnoringTimezone,
  formatWithZonedDate,
} from "@/lib/helpers/date";
import { trackEvent } from "@/lib/tracking";

const EssentialCustomerContactsHistoryPageQuery = graphql(/* GraphQL */ `
  query EssentialCustomerContactsHistoryPageQuery($from: String!, $to: String!, $codPacoteServico: Int!) {
    NuntiusGetNonBillableCustomerContacts(arg1: { from: $from, to: $to, codPacoteServico: $codPacoteServico }) {
      id
      direcaoAtendimentoId
      tipoAtendimentoId
      tipoCanalOrigemId
      numeroOrigem
      numeroDestino
      inicio
      fim
      nomePaciente
    }
  }
`);

interface EssentialContact {
  id: number;
  direcaoAtendimentoId: number;
  tipoAtendimentoId: number;
  tipoCanalOrigemId: number;
  numeroOrigem: string;
  numeroDestino: string;
  inicio: string;
  fim: string;
  nomePaciente: string | null;
}

const getDayLabel = (dateString: string): string => {
  const convertedDate = convertDateTimeIgnoringTimezone(dateString, "00:00");

  const weekday = formatIgnoringTimezone(convertedDate, "EEE").slice(0, 3);
  const formattedDate = formatIgnoringTimezone(convertedDate, "dd/MM/yyyy");

  return `${weekday}, ${formattedDate}`;
};

interface ContactsByDay {
  date: string;
  contacts: EssentialContact[];
}

const groupContactsByDay = (contacts: EssentialContact[]): ContactsByDay[] => {
  const groupedMap = new Map<string, EssentialContact[]>();

  for (const contact of contacts) {
    const date = formatDateAndTimeIgnoringTimezone(contact.inicio, "00:00", "yyyy-MM-dd");
    const existing = groupedMap.get(date) ?? [];
    groupedMap.set(date, [...existing, contact]);
  }

  return Array.from(groupedMap.entries())
    .map(([date, dayContacts]) => ({
      date,
      contacts: dayContacts,
    }))
    .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
};

interface EssentialContactItemProps {
  contact: EssentialContact;
}

const EssentialContactItem = ({ contact }: EssentialContactItemProps): JSX.Element => {
  const patientName = contact.nomePaciente ?? "Não está na sua lista de pacientes";
  const startTime = formatWithZonedDate(String(contact.inicio), "HH:mm");
  const endTime = formatWithZonedDate(String(contact.fim), "HH:mm");
  const contactChannelType = OriginChannelType[contact.tipoCanalOrigemId];
  const phoneNumber = getContactPhoneNumber(
    contact.numeroOrigem,
    contact.numeroDestino,
    contact.direcaoAtendimentoId,
  );

  return (
    <Item>
      <ItemContent>
        <div className="flex gap-2">
          <p className="text-xl text-neutral-600 font-medium mb-2">{phoneNumber}</p>
        </div>
        <ItemMainContent className="text-sm text-neutral-600">
          {patientName}
        </ItemMainContent>
        <p className="text-sm text-neutral-500">
          {`${startTime} - ${endTime}`} • {contactChannelType}
        </p>
      </ItemContent>
    </Item>
  );
};

interface EssentialContactsDayGroupProps {
  dayData: ContactsByDay;
}

const EssentialContactsDayGroup = ({
  dayData,
}: EssentialContactsDayGroupProps): JSX.Element => {
  return (
    <div className="border-b-2 border-gray-100">
      <h3 className="font-bold text-lg text-neutral-500 capitalize">
        {getDayLabel(dayData.date)}
      </h3>

      <ListRoot>
        {dayData.contacts.map((contact) => (
          <EssentialContactItem key={contact.id} contact={contact} />
        ))}
      </ListRoot>
    </div>
  );
};

const EssentialContactsHistoryPage = (): JSX.Element => {
  const navigate = useNavigate();
  const router = useRouter();
  const searchParams = useSearch({
    from: "/settings/assist/contacts-history/essential/",
  });

  const { from, to } = useMemo(() => {
    const { from: initialFromDate, to: initialToDate } = resolveContactsHistoryDates(
      searchParams,
      30,
    );

    return {
      from: formatDateAndTimeIgnoringTimezone(initialFromDate, "00:00", "yyyy-MM-dd"),
      to: formatDateAndTimeIgnoringTimezone(initialToDate, "23:59", "yyyy-MM-dd"),
    };
  }, [searchParams]);

  const queryResult = useGraphQL(
    EssentialCustomerContactsHistoryPageQuery,
    { from, to, codPacoteServico: ServicePackageEnum.Essential },
    { staleTime: FIVE_MINUTES_STALE_TIME, enabled: Boolean(from && to) },
  );

  const { data } = queryResult;

  const contacts = (data?.NuntiusGetNonBillableCustomerContacts ??
    []) as EssentialContact[];
  const contactsByDay = groupContactsByDay(contacts);

  const openAboutDrawer = (): void => {
    void router.navigate({
      to: ".",
      search: {
        ...searchParams,
        action: "about",
      },
    });
  };

  return (
    <>
      <HeaderRoot>
        <HeaderButton icon={Left} align="start" aria-label="Voltar" />
        <HeaderTitle title="Raio-X de contatos" />
        <HeaderButton
          icon={HelpCircle}
          align="end"
          aria-label="about"
          onClick={openAboutDrawer}
        />
      </HeaderRoot>

      <Page>
        <AsyncDataWrapper {...queryResult}>
          {data ? (
            <div className="flex flex-col">
              <PageTitleRoot>
                <PageTitleMainContent id="contacts-history-assist-essential-page-title">
                  Você conseguiu falar com esses pacientes?
                </PageTitleMainContent>
                <PageTitleDescription id="contacts-history-assist-essential-page-description">
                  Compartilhamos seu telefone com esses pacientes. Confira se todos foram
                  atendidos.
                </PageTitleDescription>
              </PageTitleRoot>

              <div className="flex flex-col py-2 pb-6">
                <EssentialContactsHistoryFilterContainer />
              </div>

              {contactsByDay.length > 0 ? (
                <div className="flex flex-col pt-2 gap-6 pb-60">
                  {contactsByDay.map((dayData) => (
                    <EssentialContactsDayGroup key={dayData.date} dayData={dayData} />
                  ))}
                </div>
              ) : (
                <EmptyStateRoot id="essential-contacts-history-empty-state">
                  <EmptyStateIcon icon={ExclamationCircle} />
                  <EmptyStateTitle id="essential-contacts-history-empty-state-title">
                    Nenhum contato recebido
                  </EmptyStateTitle>
                </EmptyStateRoot>
              )}
            </div>
          ) : null}

          <div className="fixed bottom-0 left-0 md:left-[72px] right-0 bg-white border-t border-neutral-200 z-50">
            <div className="px-4 py-4 flex flex-col gap-4">
              <CpsAlertV2Root
                type="success"
                htmlProps={{ id: "contacts-history-assist-essential-page-alert" }}
              >
                <CpsAlertV2IconArea>
                  <StarsLightSparkle size={24} className="fill-success-700 shrink-0" />
                </CpsAlertV2IconArea>
                <CpsAlertV2TextArea className="text-success-700">
                  <CpsAlertV2Title
                    type="success"
                    id="contacts-history-assist-essential-page-alert-title"
                  >
                    Com o Assist Pro seus pacientes não ficam sem resposta
                  </CpsAlertV2Title>
                </CpsAlertV2TextArea>
              </CpsAlertV2Root>

              <div className="flex flex-col">
                <Button
                  className="font-medium"
                  fullWidth
                  onClick={() => {
                    trackEvent("Botão Aceitar Assist Pro Clicado", {
                      pacoteServicos: ServicePackageEnum.Essential.valueOf(),
                      origem: "raio-x essencial",
                    });
                    void navigate({
                      to: "/settings/assist/subscribe-assist-pro",
                      search: { navigateBack: true },
                    });
                  }}
                >
                  Conhecer o Assist Pro
                </Button>
                <p className="text-center text-sm pt-2 text-neutral-400">
                  Os contatos podem levar até 48h para aparecer nessa lista.
                </p>
              </div>
            </div>
          </div>
        </AsyncDataWrapper>
        <ContactsHistoryEssentialMessageDrawer />
      </Page>
    </>
  );
};

export const Route = createFileRoute("/settings/assist/contacts-history/essential/")({
  component: EssentialContactsHistoryPage,
  beforeLoad: ({ context }) => {
    const { user, flags } = context;
    const showContactHistoryEssentialItem = Boolean(
      flags["habilita-historico-de-contatos-no-menu-do-assist-essencial"],
    );

    if (!isEssentialUser(user) || !showContactHistoryEssentialItem) {
      redirect({
        to: "/settings/assist",
        throw: true,
      });
    }
  },
});
